import { withPagination } from 'shared/table/hoc/withPagination';
import {
  TransactionSummaryByAccountTable,
  TransactionSummaryByAccountTableProps,
} from '../TransactionSummaryByAccountTable/TransactionSummaryByAccountTable';
import { TotalesPorCuenta } from 'core/movimientos/models/TotalesPorCuenta';
import {
  TransactionSummaryByCardTable,
  TransactionSummaryByCardTableProps,
} from '../TransactionSummaryByCardTable/TransactionSummaryByCardTable';
import { TotalesPorTarjeta } from 'core/movimientos/models/TotalesPorTarjeta';
import { SearchTotalesMovimientosAgrupadosRequest } from 'core/movimientos/models/SearchTotalesMovimientosAgrupadosRequest';
import { useCoreService } from 'core/services/useCoreService';
import { useDataFetcher } from 'shared/http';
import { useEffect, useMemo } from 'react';
import { Periodo } from 'shared/models/Periodo';
import { PagedResponseWithAggregation } from 'shared/table/models/PagedResponseWithAggregation';

const TransactionSummaryByAccountTableWithPagination = withPagination<
  TotalesPorCuenta,
  TransactionSummaryByAccountTableProps
>(TransactionSummaryByAccountTable);

const TransactionSummaryByCardTableWithPagination = withPagination<
  TotalesPorTarjeta,
  TransactionSummaryByCardTableProps
>(TransactionSummaryByCardTable);

const DEFAULT_NO_RESULT_MESSAGE =
  'No se encontraron movimientos que concuerden con los parámetros de la búsqueda';

export const TransactionSummaryTableWithPagination = (props: {
  searchParams: SearchTotalesMovimientosAgrupadosRequest;
  reloadToken?: number;
  noResultsMessage?: string;
}) => {
  const coreService = useCoreService();
  const [centroCostos, fetchCentroCostos] =
    useDataFetcher<PagedResponseWithAggregation<TotalesPorCuenta | TotalesPorTarjeta>>();

  const filtrosEstables = useMemo(() => {
    return props.searchParams;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(props.searchParams)]);

  const periodo = useMemo(() => {
    return new Periodo(props.searchParams.mes, props.searchParams.anio);
  }, [props.searchParams.mes, props.searchParams.anio]);

  const agruparPor = useMemo(
    () => props.searchParams?.agruparPor,
    [props.searchParams?.agruparPor],
  );

  useEffect(() => {
    fetchCentroCostos(() => coreService.getTotalesMovimientosAgrupados(filtrosEstables));
  }, [coreService, fetchCentroCostos, filtrosEstables, props.reloadToken]);

  const onChangePaginationStatus = async (status: {
    pageNumber: number;
    pageSize: number;
  }): Promise<PagedResponseWithAggregation<TotalesPorCuenta | TotalesPorTarjeta>> => {
    props.searchParams.page = status.pageNumber;
    props.searchParams.size = status.pageSize;
    return coreService.getTotalesMovimientosAgrupados(props.searchParams);
  };

  if (agruparPor === 'CUENTA') {
    return (
      <TransactionSummaryByAccountTableWithPagination
        pageableInit={
          centroCostos.data as PagedResponseWithAggregation<TotalesPorCuenta>
        }
        loadingState={centroCostos.isLoading}
        dataFetcherFunction={
          onChangePaginationStatus as (status: {
            pageNumber: number;
            pageSize: number;
          }) => Promise<PagedResponseWithAggregation<TotalesPorCuenta>>
        }
        periodo={periodo}
        noResultsMessages={props.noResultsMessage || DEFAULT_NO_RESULT_MESSAGE}
      />
    );
  }

  return (
    <TransactionSummaryByCardTableWithPagination
      pageableInit={
        centroCostos.data as PagedResponseWithAggregation<TotalesPorTarjeta>
      }
      loadingState={centroCostos.isLoading}
      dataFetcherFunction={
        onChangePaginationStatus as (status: {
          pageNumber: number;
          pageSize: number;
        }) => Promise<PagedResponseWithAggregation<TotalesPorTarjeta>>
      }
      noResultsMessages={props.noResultsMessage || DEFAULT_NO_RESULT_MESSAGE}
      periodo={periodo}
    />
  );
};
